import { useEffect, useState } from "react"
import { useLocation } from "react-router-dom"
import { TableComponent } from "./TableComponent"
import { MultiTableComponent } from "./MultiTableComponent"
import { BookingLRTrackStatusData, fetchLRStatusData } from "./jotaiState/reportStateAtom"
import LRStatusModel from "../content/Reports/BkngDelvryTrackingReport/LRStatusModel"
import { useAtom } from "jotai"
import { useSelector } from "react-redux"
import useToken from "../helpers/useTokens"
import { companySettingsAtom } from "./jotaiState/commonStateAtoms"

const REPORT_DB_NAME = "cargo-report-cache"
const REPORT_DB_STORE = "reports"
const MAX_INDEXED_DB_REPORTS = 10

const openReportDb = () => {
  return new Promise((resolve) => {
    if (typeof window === "undefined" || !window.indexedDB) {
      resolve(null)
      return
    }

    const request = window.indexedDB.open(REPORT_DB_NAME, 1)

    request.onupgradeneeded = () => {
      const database = request.result
      if (!database.objectStoreNames.contains(REPORT_DB_STORE)) {
        database.createObjectStore(REPORT_DB_STORE, { keyPath: "id" })
      }
    }

    request.onsuccess = () => resolve(request.result)
    request.onerror = () => {
      console.error("Failed to open report cache database", request.error)
      resolve(null)
    }
  })
}

const getIndexedDbReport = async (rid) => {
  const database = await openReportDb()
  if (!database) return null

  return new Promise((resolve) => {
    const transaction = database.transaction(REPORT_DB_STORE, "readonly")
    const request = transaction.objectStore(REPORT_DB_STORE).get(rid)

    request.onsuccess = () => {
      resolve(request.result?.payload || null)
    }

    request.onerror = () => {
      console.error("Failed to read cached report from IndexedDB", request.error)
      resolve(null)
    }

    transaction.oncomplete = () => {
      database.close()
    }

    transaction.onabort = transaction.onerror = () => {
      database.close()
    }
  })
}

const pruneIndexedDbReports = async (activeRid) => {
  const database = await openReportDb()
  if (!database) return

  await new Promise((resolve) => {
    const transaction = database.transaction(REPORT_DB_STORE, "readwrite")
    const reportStore = transaction.objectStore(REPORT_DB_STORE)

    if (typeof reportStore.getAllKeys !== "function") {
      resolve()
      return
    }

    const request = reportStore.getAllKeys()

    request.onsuccess = () => {
      const reportKeys = (request.result || [])
        .map((key) => String(key))
        .sort((left, right) => Number(left) - Number(right))
      const removableKeys = reportKeys.filter((key) => key !== activeRid)
      const excessCount = Math.max(0, reportKeys.length - MAX_INDEXED_DB_REPORTS)

      removableKeys.slice(0, excessCount).forEach((key) => {
        reportStore.delete(key)
      })
    }

    request.onerror = () => {
      console.error("Failed to prune IndexedDB report cache", request.error)
    }

    transaction.oncomplete = () => {
      database.close()
      resolve()
    }

    transaction.onabort = transaction.onerror = () => {
      database.close()
      resolve()
    }
  })
}

const cacheReportInIndexedDb = async (rid, reportData) => {
  const database = await openReportDb()
  if (!database) return false

  const didPersist = await new Promise((resolve) => {
    const transaction = database.transaction(REPORT_DB_STORE, "readwrite")
    const reportStore = transaction.objectStore(REPORT_DB_STORE)

    reportStore.put({
      id: rid,
      createdAt: Date.now(),
      payload: reportData,
    })

    transaction.oncomplete = () => {
      database.close()
      resolve(true)
    }

    transaction.onabort = transaction.onerror = () => {
      console.error("Failed to cache report in IndexedDB", transaction.error)
      database.close()
      resolve(false)
    }
  })

  if (didPersist) {
    await pruneIndexedDbReports(rid)
  }

  return didPersist
}

const persistReport = async (rid, reportData) => {
  const indexedDbStored = await cacheReportInIndexedDb(rid, reportData)
  return indexedDbStored
}

const ViewReport = (props) => {
  const { loginData } = useToken()
  const location = useLocation()
  const [report, setReport] = useState(null)
  const [, fetchLRStatus] = useAtom(fetchLRStatusData)
  const [openLrModal, setOpenLrModal] = useState(false)
  const [LRTrackStatusData] = useAtom(BookingLRTrackStatusData)
  const handleOpenModal = (lrData) => {
    if (report.defaultCurrencyID === 2) {
      setOpenLrModal(true)
      const obj = {
        intCompanyID: loginData.intCompanyID,
        intBranchID: loginData.intBranchID,
        strLRNo: lrData.LRNO,
        intUserID: loginData.intUserID,
        BookingID : lrData.BookingID
      }
      fetchLRStatus(obj)
    }
  }
  useEffect(() => {
    let isMounted = true

    const loadReport = async () => {
      const rid = new URLSearchParams(location.search).get("rid")

      if (!rid) {
        if (isMounted) {
          setReport(null)
        }
        return
      }

      let reportData = await getIndexedDbReport(rid)

      if (!reportData && window.opener?.generatedReports?.[rid]) {
        reportData = JSON.parse(JSON.stringify(window.opener.generatedReports[rid]))
      }

      if (isMounted) {
        setReport(reportData)
      }

      if (reportData && window.opener?.generatedReports?.[rid]) {
        const didPersist = await persistReport(rid, reportData)
        if (didPersist && window.opener?.generatedReports?.[rid]) {
          delete window.opener.generatedReports[rid]
        }
      }
    }

    loadReport()

    return () => {
      isMounted = false
    }
  }, [location.search])

  if (!report) return null

  // ✅ NEW CONDITION
  if (report.isMultiTable && Array.isArray(report.tables)) {
    return (
      <MultiTableComponent
        reportName={report.reportName}
        tables={report.tables}
        isShowManageColumn={report.isShowManageColumn}
        manageColumnID={report.manageColumnID}
        hideHeader={report.hideHeader}
        isShowPdfPrint={report.isShowPdfPrint}
        colSpanMarker={report.colSpanMarker}
        showColumnBorders={report.showColumnBorders}
        isIncreFontSize={report.isIncreFontSize}
        isClampHeaderToTwoLines={report.isClampHeaderToTwoLines}
        isWrapEntryText={report.isWrapEntryText}
        enableHorizontalScroll={report.enableHorizontalScroll}
        minTableWidth={report.minTableWidth}
      />
    )
  }
  return (
    <>
      <TableComponent
        reportName={report.reportName}
        criteriaHeaders={report.criteriaHeaders}
        criteriaData={report.criteriaData}
        tableData={report.tableData}
        mainHeader={report.mainHeader}
        isShowManageColumn={report.isShowManageColumn}
        manageColumnID={report.manageColumnID}
        hideHeader={report.hideHeader}
        isShowPdfPrint={report.isShowPdfPrint}
        colSpanMarker={report.colSpanMarker}
        isOpenPopupForSta={report.isOpenPopupForSta}
        onLRClick={(row) => handleOpenModal(row)}
        totalSubKey={report.totalSubKey}
        isInvoiceRedirect={report.isInvoiceRedirect}
        serialNokey={report.serialNokey}
        tableStyle={report.tableStyle}
        isCSVRequired={report.isCSVRequired}
        isMultiSplit={report.isMultiSplit}
        fieldColor={report.fieldColor}
        showColumnBorders={report.showColumnBorders}
        isIncreFontSize={report.isIncreFontSize}
        isClampHeaderToTwoLines={report.isClampHeaderToTwoLines}
        isWrapEntryText={report.isWrapEntryText}
        enableHorizontalScroll={report.enableHorizontalScroll}
        minTableWidth={report.minTableWidth}
      />
      {openLrModal && (
        <LRStatusModel
          open={openLrModal}
          setOpenLrModal={setOpenLrModal}
          LRTrackStatusData={LRTrackStatusData}
          DefaultCurrencyID={report.defaultCurrencyID}
        />
      )}
    </>
  )
}

export default ViewReport
